"use client";

import classNames from "classnames";

import Link from "next/link";

import { LinkWithLangType } from "@stadium/shared/src/constants/stores/routes";

import type { LinkRouteDefaults } from "@stadium/shared/src/components/link-with-lang";

import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import ShimmerWrapper from "@stadium/ui/src/components/loaders/shimmer-wrapper";
import ShadowCard from "@sharedComponents/cards/shadow-card";

import CarouselContainer from "@stadium/ui/src/components/carousel-container";

import styles from "./main.module.scss";

const renderShimmerLoader = () => {
  return (
    <ShimmerWrapper
      length={5}
      cardClass={styles.colCard}
      wrapperClass={`${styles.productCardsWrapper} ${styles.xlFiveCard}`}
    >
      <ShadowCard />
    </ShimmerWrapper>
  );
};

export type RowData = {
  slides: number;
  rows: number;
};

export type SlidesPerRow = {
  slidesPerRow: RowData;
  superLargeDesktop: RowData;
  web: RowData;
  ipad: RowData;
  tablet?: RowData;
  mobile: RowData;
};

export type HorizontalCarouselWrapperProps = {
  children?: React.ReactNode;
  className?: string;
  isGridCarousel?: boolean;
  title?: string;
  description?: string;
  btnText?: string;
  btnLink?: string;
  linkToNewTab: boolean;
  slidesPerRow: SlidesPerRow;
  dropDown?: React.ReactNode;
  headerChildren?: React.ReactNode;
  showLoader?: boolean;
  showWithCarousel?: boolean;
  carouselContainerClassName?: string;
  headerContainerClassName?: string;
  titleContainerClassName?: string;
  descriptionTextClass?: string;
  linkDataAndType?: { type: LinkWithLangType; data?: any };
  btnLinkQueryParams?: Record<string, string>;
  linkRouteDefaults?: LinkRouteDefaults;
};

type HeaderButtonProps = {
  isLoading?: boolean;
  btnText?: string;
  linkHref?: string;
  linkToNewTab?: boolean;
  linkDataAndType?: { type: LinkWithLangType; data?: any };
  children: React.ReactNode;
  queryParams?: Record<string, string>;
  linkRouteDefaults?: LinkRouteDefaults;
};

const HeaderButton = ({
  isLoading,
  btnText,
  linkHref,
  linkToNewTab,
  linkDataAndType,
  children,
  queryParams,
  linkRouteDefaults,
}: HeaderButtonProps) => {
  if (!btnText && !linkHref) return null;

  if (isLoading) {
    return (
      <div className={styles.primaryLink} aria-disabled>
        {children}
      </div>
    );
  }

  const linkProps = {
    className: styles.primaryLink,
    target: linkToNewTab ? "_blank" : "_self",
    "aria-labelledby": btnText,
    query: queryParams,
  };

  if (linkDataAndType?.type) {
    return (
      <LinkWithLang
        {...linkRouteDefaults}
        type={linkDataAndType.type}
        data={linkDataAndType.data}
        {...linkProps}
      >
        {children}
      </LinkWithLang>
    );
  }

  return (
    <Link href={linkHref ?? ""} {...linkProps}>
      {children}
    </Link>
  );
};

const HorizontalCarouselWrapper = (props: HorizontalCarouselWrapperProps) => {
  const {
    children,
    isGridCarousel,
    title,
    description,
    btnText,
    btnLink,
    linkToNewTab,
    className,
    slidesPerRow,
    dropDown,
    headerChildren,
    showLoader,
    showWithCarousel = true,
    carouselContainerClassName,
    headerContainerClassName,
    titleContainerClassName,
    descriptionTextClass,
    linkDataAndType,
    btnLinkQueryParams,
    linkRouteDefaults,
  } = props;

  const linkHref = btnLink?.trim();
  const titleToShow = title?.trim();
  const descriptionToShow = description?.trim();
  const btnTextToShow = btnText?.trim();

  return (
    <div
      className={classNames(styles.horizontalCarouselWrapper, className, {
        [styles.gridCarousel]: isGridCarousel,
      })}
    >
      <div className={classNames(styles.headerContainer, headerContainerClassName)}>
        <div className={classNames(styles.titleContainer, titleContainerClassName)}>
          <div className={styles.titleAndDropDownContainer}>
            {titleToShow && <h2 className={styles.sliceTitle}>{titleToShow}</h2>}
            {dropDown && <span className={styles.mobileHidden}>{dropDown}</span>}
          </div>
          <HeaderButton
            isLoading={showLoader}
            btnText={btnTextToShow}
            linkHref={linkHref}
            linkToNewTab={linkToNewTab}
            linkDataAndType={linkDataAndType}
            queryParams={btnLinkQueryParams}
            linkRouteDefaults={linkRouteDefaults}
          >
            <span className={styles.primaryLinkText}>{btnTextToShow}</span>
            <div className={styles.primaryLinkIconContainer}>
              <span className={styles.primaryLinkIcon}></span>
            </div>
          </HeaderButton>
        </div>
        {dropDown && <div className={styles.mobileVisible}>{dropDown}</div>}
        {descriptionToShow && (
          <div className={descriptionTextClass || styles.descriptionContainer}>
            {descriptionToShow}
          </div>
        )}
        {headerChildren}
      </div>
      {showLoader && renderShimmerLoader()}
      {!showLoader && showWithCarousel && (
        <CarouselContainer
          slidesPerRowValues={slidesPerRow}
          className={classNames(styles.categoryCarousel, carouselContainerClassName)}
        >
          {children}
        </CarouselContainer>
      )}
      {!showLoader && !showWithCarousel && children}
    </div>
  );
};

export default HorizontalCarouselWrapper;
